import type { ShellId } from './types';
import { isBlockedNavigationFieldName } from './query';

export const NAVIGATION_SESSION_PREFIX = 'aa:navigation:v1:';
export const NAVIGATION_PURGE_EVENT = 'aa:navigation-purge';
export const NAVIGATION_SESSION_TTL_MS = 8 * 60 * 60 * 1000;
export const NAVIGATION_SESSION_MAX_BYTES = 256_000;

const NAVIGATION_SESSION_PURGE_MARKER_KEY = `${NAVIGATION_SESSION_PREFIX}__purge_pending__`;

export type SafeSessionValue =
  | null
  | boolean
  | number
  | string
  | SafeSessionValue[]
  | { [key: string]: SafeSessionValue };

export interface SessionStateIdentity {
  scopeId: string;
  userId: string;
  workspace: ShellId;
  routeId: string;
  entityId?: string | number | null;
  purpose: string;
  version: number;
}

export interface SessionStateAdapter<T> {
  initial(): T;
  encode(value: T): SafeSessionValue;
  decode(value: unknown): T | null;
  isDirty(value: T, initial: T): boolean;
  hasUnpersistableChanges?(value: T, initial: T): boolean;
}

export interface StoredSessionState<T> {
  value: T;
  fileReselectionRequired: boolean;
  revision: number;
}

export interface SessionStore {
  read<T>(
    identity: SessionStateIdentity,
    adapter: SessionStateAdapter<T>,
  ): StoredSessionState<T> | null;
  write<T>(
    identity: SessionStateIdentity,
    adapter: SessionStateAdapter<T>,
    value: T,
    fileReselectionRequired: boolean,
    revision?: number,
  ): void;
  remove(identity: SessionStateIdentity): void;
  purge(): void;
}

type DraftScalar = string | number | boolean | null;

const RESERVED_PROPERTY_NAMES = new Set(['__proto__', 'prototype', 'constructor']);
const SHELL_IDS = new Set<ShellId>([
  'public',
  'auth',
  'reception',
  'sales',
  'manager',
  'hr',
  'accountant',
  'marketing',
  'developer',
  'employee',
  'student',
  'instructor',
  'placement',
  'summer',
  'mail',
  'diagnostics',
  'error',
]);

function fail(message: string): never {
  throw new Error(`Unsafe navigation session value: ${message}`);
}

function ownDataDescriptor(
  value: object,
  key: PropertyKey,
): PropertyDescriptor | undefined {
  const descriptor = Object.getOwnPropertyDescriptor(value, key);
  return descriptor && 'value' in descriptor ? descriptor : undefined;
}

function snapshotSafeValue(
  value: unknown,
  active: WeakSet<object>,
  snapshots: WeakMap<object, SafeSessionValue[] | { [key: string]: SafeSessionValue }>,
): SafeSessionValue {
  if (value === null || typeof value === 'string' || typeof value === 'boolean') return value;
  if (typeof value === 'number') {
    if (!Number.isFinite(value)) fail('numbers must be finite');
    return value;
  }
  if (typeof value !== 'object') fail(`unsupported ${typeof value}`);

  const objectValue = value as object;
  if (active.has(objectValue)) fail('cyclic values are not allowed');
  const existingSnapshot = snapshots.get(objectValue);
  if (existingSnapshot) return existingSnapshot;
  active.add(objectValue);
  try {
    const prototype = Object.getPrototypeOf(objectValue) as unknown;
    const keys = Reflect.ownKeys(objectValue);
    if (Array.isArray(objectValue)) {
      if (prototype !== Array.prototype) fail('array prototype must be ordinary');
      const lengthDescriptor = ownDataDescriptor(objectValue, 'length');
      if (!lengthDescriptor
        || typeof lengthDescriptor.value !== 'number'
        || !Number.isSafeInteger(lengthDescriptor.value)
        || lengthDescriptor.value < 0) fail('invalid array length');
      const length = lengthDescriptor.value;
      if (keys.length !== length + 1 || keys[keys.length - 1] !== 'length') {
        fail('arrays must be dense and have no extended properties');
      }
      const snapshot: SafeSessionValue[] = [];
      Object.setPrototypeOf(snapshot, null);
      snapshots.set(objectValue, snapshot);
      for (let index = 0; index < length; index += 1) {
        const descriptor = ownDataDescriptor(objectValue, String(index));
        if (!descriptor || descriptor.enumerable !== true) fail('arrays must be dense');
        Object.defineProperty(snapshot, String(index), {
          configurable: true,
          enumerable: true,
          value: snapshotSafeValue(descriptor.value, active, snapshots),
          writable: true,
        });
      }
      return Object.freeze(snapshot) as SafeSessionValue[];
    }

    if (prototype !== Object.prototype && prototype !== null) {
      fail('records must have a plain or null prototype');
    }
    const snapshot = Object.create(null) as { [key: string]: SafeSessionValue };
    snapshots.set(objectValue, snapshot);
    for (const key of keys) {
      if (typeof key !== 'string') fail('symbol properties are not allowed');
      if (RESERVED_PROPERTY_NAMES.has(key) || isBlockedNavigationFieldName(key)) {
        fail(`blocked property name: ${key}`);
      }
      const descriptor = ownDataDescriptor(objectValue, key);
      if (!descriptor || descriptor.enumerable !== true) {
        fail('accessor and non-enumerable properties are not allowed');
      }
      snapshot[key] = snapshotSafeValue(descriptor.value, active, snapshots);
    }
    return Object.freeze(snapshot) as { [key: string]: SafeSessionValue };
  } finally {
    active.delete(objectValue);
  }
}

function createSafeSessionSnapshot(value: unknown): SafeSessionValue {
  try {
    return snapshotSafeValue(
      value,
      new WeakSet<object>(),
      new WeakMap<object, SafeSessionValue[] | { [key: string]: SafeSessionValue }>(),
    );
  } catch (error) {
    if (error instanceof Error && error.message.startsWith('Unsafe navigation session value:')) {
      throw error;
    }
    throw new Error('Unsafe navigation session value: inspection failed', { cause: error });
  }
}

export function assertSafeSessionValue(
  value: unknown,
): asserts value is SafeSessionValue {
  createSafeSessionSnapshot(value);
}

function isRecord(value: unknown): value is Record<string, unknown> {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) return false;
  try {
    const prototype = Object.getPrototypeOf(value) as unknown;
    return prototype === Object.prototype || prototype === null;
  } catch {
    return false;
  }
}

function validateScalar(value: unknown, expected: DraftScalar): value is DraftScalar {
  if (expected === null) return value === null;
  if (typeof value !== typeof expected) return false;
  return typeof value !== 'number' || Number.isFinite(value);
}

export function createScalarDraftAdapter<
  const T extends Record<string, DraftScalar>,
>(defaults: T): SessionStateAdapter<T> {
  const safeDefaults = createSafeSessionSnapshot(defaults);
  if (!isRecord(safeDefaults)) fail('scalar adapter defaults must be a record');

  const keys = Object.keys(safeDefaults) as (keyof T & string)[];
  const snapshot = Object.create(null) as Record<string, DraftScalar>;
  for (const key of keys) {
    const descriptor = ownDataDescriptor(safeDefaults, key);
    if (!descriptor || descriptor.enumerable !== true || !validateScalar(descriptor.value, descriptor.value)) {
      fail(`invalid scalar adapter default: ${key}`);
    }
    if (typeof descriptor.value === 'number' && !Number.isFinite(descriptor.value)) {
      fail(`invalid scalar adapter default: ${key}`);
    }
    snapshot[key] = descriptor.value as DraftScalar;
  }
  Object.freeze(snapshot);
  const initial = (): T => ({ ...snapshot }) as T;

  return Object.freeze({
    initial,
    encode(value: T): SafeSessionValue {
      if (!isRecord(value)) fail('scalar adapter value must be a record');
      const encoded: Record<string, SafeSessionValue> = {};
      for (const key of keys) {
        const descriptor = ownDataDescriptor(value, key);
        const expected = snapshot[key];
        if (!descriptor
          || descriptor.enumerable !== true
          || !validateScalar(descriptor.value, expected)) {
          fail(`invalid scalar adapter field: ${key}`);
        }
        encoded[key] = descriptor.value as DraftScalar;
      }
      assertSafeSessionValue(encoded);
      return encoded;
    },
    decode(raw: unknown): T | null {
      let safeRaw: SafeSessionValue;
      try {
        safeRaw = createSafeSessionSnapshot(raw);
      } catch {
        return null;
      }
      if (!isRecord(safeRaw)) return null;
      const decoded = initial() as Record<string, DraftScalar>;
      for (const key of keys) {
        const descriptor = ownDataDescriptor(safeRaw, key);
        if (!descriptor) continue;
        const expected = snapshot[key];
        if (descriptor.enumerable !== true || !validateScalar(descriptor.value, expected)) {
          return null;
        }
        decoded[key] = descriptor.value as DraftScalar;
      }
      return decoded as T;
    },
    isDirty(value: T, initialValue: T): boolean {
      return keys.some((key) => value[key] !== initialValue[key]);
    },
  });
}

function requiredIdentityString(identity: object, key: keyof SessionStateIdentity): string {
  const descriptor = ownDataDescriptor(identity, key);
  if (!descriptor || typeof descriptor.value !== 'string' || descriptor.value.trim().length === 0) {
    throw new Error(`Invalid navigation session identity: ${String(key)}`);
  }
  return descriptor.value;
}

/** @internal Shared with hooks so identity changes never reuse a delimiter-based key. */
export function buildSessionStateStorageKey(identity: SessionStateIdentity): string {
  if (!isRecord(identity)) throw new Error('Invalid navigation session identity');
  const scopeId = requiredIdentityString(identity, 'scopeId');
  const userId = requiredIdentityString(identity, 'userId');
  const workspace = requiredIdentityString(identity, 'workspace') as ShellId;
  const routeId = requiredIdentityString(identity, 'routeId');
  const purpose = requiredIdentityString(identity, 'purpose');
  if (!SHELL_IDS.has(workspace)) throw new Error('Invalid navigation session identity: workspace');

  const versionDescriptor = ownDataDescriptor(identity, 'version');
  const version = versionDescriptor?.value;
  if (!Number.isSafeInteger(version) || (version as number) < 0) {
    throw new Error('Invalid navigation session identity: version');
  }
  const entityDescriptor = ownDataDescriptor(identity, 'entityId');
  const entityId = entityDescriptor?.value ?? null;
  if (typeof entityId === 'number'
    ? !Number.isSafeInteger(entityId) || entityId < 0
    : typeof entityId === 'string'
      ? entityId.trim().length === 0
      : entityId !== null) {
    throw new Error('Invalid navigation session identity: entityId');
  }

  const tuple = [scopeId, userId, workspace, routeId, entityId, purpose, version];
  return `${NAVIGATION_SESSION_PREFIX}${encodeURIComponent(JSON.stringify(tuple))}`;
}

function byteLength(value: string): number {
  return new TextEncoder().encode(value).byteLength;
}

function bestEffortRemove(storage: Storage, key: string): boolean {
  try {
    storage.removeItem(key);
    return true;
  } catch {
    // Storage can be disabled. Removal is intentionally best-effort.
    return false;
  }
}

function prefixedStorageKeys(storage: Storage): string[] | null {
  const keys: string[] = [];
  try {
    const length = storage.length;
    for (let index = 0; index < length; index += 1) {
      const key = storage.key(index);
      if (key === null) return null;
      if (key.startsWith(NAVIGATION_SESSION_PREFIX)) keys.push(key);
    }
  } catch {
    return null;
  }
  return keys;
}

function completePendingPurge(storage: Storage): boolean {
  const keys = prefixedStorageKeys(storage);
  if (keys === null) return false;

  for (const key of keys) {
    if (key === NAVIGATION_SESSION_PURGE_MARKER_KEY) continue;
    if (!bestEffortRemove(storage, key)) return false;
  }

  const remainingKeys = prefixedStorageKeys(storage);
  if (remainingKeys === null
    || remainingKeys.some((key) => key !== NAVIGATION_SESSION_PURGE_MARKER_KEY)) {
    return false;
  }

  return bestEffortRemove(storage, NAVIGATION_SESSION_PURGE_MARKER_KEY);
}

function hasPendingPurge(storage: Storage): boolean | null {
  try {
    return storage.getItem(NAVIGATION_SESSION_PURGE_MARKER_KEY) !== null;
  } catch {
    return null;
  }
}

function invalidEntry(storage: Storage, key: string): null {
  bestEffortRemove(storage, key);
  return null;
}

function readExactEnvelope(raw: unknown): {
  writtenAt: number;
  value: SafeSessionValue;
  fileReselectionRequired: boolean;
  revision: number;
} | null {
  if (!isRecord(raw)) return null;
  let keys: PropertyKey[];
  try {
    keys = Reflect.ownKeys(raw);
  } catch {
    return null;
  }
  const hasRequiredKeys = !keys.includes('writtenAt')
    || !keys.includes('value')
    || !keys.includes('fileReselectionRequired');
  const legacyEnvelope = keys.length === 3 && !keys.includes('revision');
  const revisionedEnvelope = keys.length === 4 && keys.includes('revision');
  if (hasRequiredKeys || (!legacyEnvelope && !revisionedEnvelope)) return null;
  const writtenAt = ownDataDescriptor(raw, 'writtenAt')?.value;
  const value = ownDataDescriptor(raw, 'value')?.value;
  const fileReselectionRequired = ownDataDescriptor(raw, 'fileReselectionRequired')?.value;
  const revision = revisionedEnvelope
    ? ownDataDescriptor(raw, 'revision')?.value
    : 0;
  if (!Number.isSafeInteger(writtenAt)
    || (writtenAt as number) < 0
    || typeof fileReselectionRequired !== 'boolean'
    || !Number.isSafeInteger(revision)
    || (revision as number) < 0) return null;
  try {
    assertSafeSessionValue(value);
  } catch {
    return null;
  }
  return {
    writtenAt: writtenAt as number,
    value,
    fileReselectionRequired,
    revision: revision as number,
  };
}

export function createSessionStore(
  storage: Storage,
  now: () => number,
): SessionStore {
  return {
    read<T>(identity: SessionStateIdentity, adapter: SessionStateAdapter<T>) {
      const key = buildSessionStateStorageKey(identity);
      const pendingPurge = hasPendingPurge(storage);
      if (pendingPurge === null) return null;
      if (pendingPurge) {
        completePendingPurge(storage);
        return null;
      }
      let raw: string | null;
      try {
        raw = storage.getItem(key);
      } catch {
        return null;
      }
      if (raw === null) return null;
      if (byteLength(raw) > NAVIGATION_SESSION_MAX_BYTES) return invalidEntry(storage, key);

      let parsed: unknown;
      try {
        parsed = JSON.parse(raw) as unknown;
      } catch {
        return invalidEntry(storage, key);
      }
      const envelope = readExactEnvelope(parsed);
      const currentTime = now();
      if (!envelope
        || !Number.isSafeInteger(currentTime)
        || currentTime < envelope.writtenAt
        || currentTime - envelope.writtenAt >= NAVIGATION_SESSION_TTL_MS) {
        return invalidEntry(storage, key);
      }

      let value: T | null;
      try {
        value = adapter.decode(envelope.value);
      } catch {
        value = null;
      }
      if (value === null) return invalidEntry(storage, key);
      return {
        value,
        fileReselectionRequired: envelope.fileReselectionRequired,
        revision: envelope.revision,
      };
    },
    write<T>(
      identity: SessionStateIdentity,
      adapter: SessionStateAdapter<T>,
      value: T,
      fileReselectionRequired: boolean,
      revision = 0,
    ) {
      const key = buildSessionStateStorageKey(identity);
      try {
        const pendingPurge = hasPendingPurge(storage);
        if (pendingPurge === null
          || (pendingPurge && !completePendingPurge(storage))) {
          throw new Error('Navigation session purge is still pending');
        }
        if (typeof fileReselectionRequired !== 'boolean') {
          throw new Error('Invalid navigation session file marker');
        }
        if (!Number.isSafeInteger(revision) || revision < 0) {
          throw new Error('Invalid navigation session revision');
        }
        const writtenAt = now();
        if (!Number.isSafeInteger(writtenAt) || writtenAt < 0) {
          throw new Error('Invalid navigation session timestamp');
        }
        const encoded = createSafeSessionSnapshot(adapter.encode(value));
        const envelope = Object.create(null) as {
          writtenAt: number;
          value: SafeSessionValue;
          fileReselectionRequired: boolean;
          revision: number;
        };
        Object.defineProperty(envelope, 'writtenAt', {
          enumerable: true,
          value: writtenAt,
        });
        Object.defineProperty(envelope, 'value', {
          enumerable: true,
          value: encoded,
        });
        Object.defineProperty(envelope, 'fileReselectionRequired', {
          enumerable: true,
          value: fileReselectionRequired,
        });
        Object.defineProperty(envelope, 'revision', {
          enumerable: true,
          value: revision,
        });
        const serialized = JSON.stringify(envelope);
        if (byteLength(serialized) > NAVIGATION_SESSION_MAX_BYTES) {
          throw new Error('Navigation session value exceeds 256000 bytes');
        }
        storage.setItem(key, serialized);
      } catch (error) {
        bestEffortRemove(storage, key);
        throw error instanceof Error
          ? error
          : new Error(`Navigation session write failed: ${String(error)}`, { cause: error });
      }
    },
    remove(identity: SessionStateIdentity) {
      bestEffortRemove(storage, buildSessionStateStorageKey(identity));
    },
    purge() {
      let markerWritten = false;
      try {
        storage.setItem(NAVIGATION_SESSION_PURGE_MARKER_KEY, '1');
        markerWritten = true;
      } catch {
        // A full quota can reject the marker while enumeration/removal still
        // works, so the synchronous erase below remains authoritative.
      }
      const completed = completePendingPurge(storage);
      if (completed || markerWritten) return;

      // Partial deletion may have freed enough quota to establish a durable
      // fail-closed marker for whatever could not be removed on this pass.
      try {
        storage.setItem(NAVIGATION_SESSION_PURGE_MARKER_KEY, '1');
        completePendingPurge(storage);
      } catch {
        // No stronger durable guarantee is available when every Storage write
        // and enumeration/removal recovery path is unavailable.
      }
    },
  };
}
